import { useEffect, useState } from 'react';
import { fetchWallTourCursors } from './api';
import type { QueueItem } from './anomalyQueue';
import { WALL_QUEUE_REFRESH_MS } from './wallQueueData';
import {
  activeTourStop,
  newTourCardItems,
  tourChipLabel,
  tourSuspended,
  type WallTourCursors,
  type WallTourStop,
} from './wallTour';

/**
 * Wall risk-tour React layer (#272; PART XI Ruling 2) — the hooks that bind
 * the pure `wallTour.ts` engine to the wall, plus the corner chip and the
 * compact SITREP card.
 *
 * The card is panel-vocabulary, never map colour, and NEVER wears URGENT-RED
 * (Ruling 2.7 — urgents preempt the tour, so a card can never be the bearer
 * of a live urgent). It carries the ticker's grammar: "clear at your desk" —
 * the wall takes zero operational input (read-only law); the desk COP's
 * queue panel holds the Clear control.
 */

/** Tour clock re-evaluation cadence — minute-grade windows need no better. */
const TOUR_TICK_MS = 5_000;

/** Poll the per-stop cleared-through cursors on the wall cadence (≤30 s — the
 *  #264-accepted propagation class for desk-state reaching the wall). */
export function useWallTourCursors(refreshMs: number = WALL_QUEUE_REFRESH_MS): WallTourCursors {
  const [cursors, setCursors] = useState<WallTourCursors>({});
  useEffect(() => {
    let cancelled = false;
    const ctl = new AbortController();
    const refresh = async (): Promise<void> => {
      try {
        const next = await fetchWallTourCursors(ctl.signal);
        if (!cancelled) setCursors(next);
      } catch (err) {
        if ((err as { name?: string }).name !== 'AbortError') {
          console.error('useWallTourCursors refresh failed', err);
        }
      }
    };
    void refresh();
    const timer = setInterval(() => void refresh(), refreshMs);
    return () => {
      cancelled = true;
      ctl.abort();
      clearInterval(timer);
    };
  }, [refreshMs]);
  return cursors;
}

export interface WallTourState {
  /** The active stop, or null (default view / suspended / killed). */
  stop: WallTourStop | null;
  /** True while an unacked urgent holds the wall (red preempts, Ruling 2.3). */
  suspended: boolean;
}

/**
 * The live tour state: re-evaluates the clock window every few seconds and
 * suspends on any unacked urgent in the shared queue sweep. `minuteOverride`
 * is the QA clock seam (`?tourMinute=NN`); `enabled=false` is the `?tour=0`
 * kill-switch.
 */
export function useWallTour(opts: {
  enabled: boolean;
  minuteOverride?: number | null;
  items: QueueItem[];
}): WallTourState {
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), TOUR_TICK_MS);
    return () => clearInterval(t);
  }, []);
  const suspended = tourSuspended(opts.items);
  if (!opts.enabled) return { stop: null, suspended };
  return {
    stop: activeTourStop(nowMs, { suspended, minuteOverride: opts.minuteOverride }),
    suspended,
  };
}

/** The corner tour chip (Ruling 2.4) — PART X corner-tag grammar: a compact
 *  overlay, never a layout row, never floating over the hero centre. */
export function WallTourChip({ stop }: { stop: WallTourStop }): JSX.Element {
  return (
    <div
      data-testid="wall-tour-chip"
      className="pointer-events-none flex items-center gap-1.5 rounded bg-black/70 px-2.5 py-1 backdrop-blur"
    >
      <span className="inline-block h-2 w-2 rounded-full bg-sky-400" aria-hidden />
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-200">
        {tourChipLabel(stop)}
      </span>
    </div>
  );
}

/**
 * The compact tour SITREP card (Ruling 2.5): ONE card per stop, surfaced ONLY
 * when the stop's domain has something NEW since its cleared-through cursor —
 * nothing new → no card (a quiet tour stop is the success state; callers
 * render nothing). Headline count + the newest item, never a list; the
 * "clear at your desk" line is the read-only law made legible.
 */
export function WallTourSitrepCard({
  stop,
  items,
  cursors,
  /** GOC = compact hero-corner overlay; PEOC = seats as a flank tile
   *  (Ruling 2.5 placement grammar — floating cards over a strip hero stay
   *  forbidden). */
  variant,
}: {
  stop: WallTourStop;
  items: QueueItem[];
  cursors: WallTourCursors;
  variant: 'hero-corner' | 'flank-tile';
}): JSX.Element | null {
  const fresh = newTourCardItems(items, stop, cursors[stop.id] ?? null);
  if (fresh.length === 0) return null;
  const newest = [...fresh].sort(
    (a, b) => (Date.parse(b.timeIso) || 0) - (Date.parse(a.timeIso) || 0),
  )[0]!;

  const body = (
    <>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-sky-300">
        Risk tour · {stop.label} — new since last review
      </p>
      <p className="mt-0.5 truncate text-sm font-bold text-slate-100">
        {fresh.length} new · {newest.hazardIcon} {newest.title}
      </p>
      <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">
        clear at your desk
      </p>
    </>
  );

  if (variant === 'flank-tile') {
    return (
      <div
        data-testid="wall-tour-card"
        data-variant="flank-tile"
        className="flex h-full w-full flex-col justify-center border border-sky-900/60 bg-slate-950 px-4"
      >
        {body}
      </div>
    );
  }
  return (
    <div
      data-testid="wall-tour-card"
      data-variant="hero-corner"
      className="pointer-events-none max-w-[360px] rounded-md border border-sky-800/70 bg-slate-950/90 px-3 py-2 backdrop-blur"
    >
      {body}
    </div>
  );
}
